import { z } from 'zod';

/** §15.4 — Fiyatlandırma ayarları şemaları. FE ve BE paylaşır. */

const pctField = (label: string, max = 1000) =>
  z
    .number({ message: `${label} sayı olmalı.` })
    .min(0, `${label} negatif olamaz.`)
    .max(max, `${label} en fazla %${max} olabilir.`);

export const marginBasisSchema = z.enum(['COST', 'SALE_PRICE']);
export const commissionModeSchema = z.enum(['FIXED', 'AMAZON', 'MAX_OF_BOTH']);
export const fxModeSchema = z.enum(['AUTO', 'MANUAL']);
export const priceRoundingSchema = z.enum(['NONE', 'CEIL_10', 'CEIL_100', 'PSYCH_80', 'PSYCH_99']);
export const listingPriceModeSchema = z.enum([
  'NORMAL',
  'MIN',
  'BUYBOX',
  'LOWEST',
  'BUYBOX_ELSE_LOWEST',
]);

export const updatePricingProfileSchema = z.object({
  marginBasis: marginBasisSchema.optional(),
  includeAmazonFeeTax: z.boolean().optional(),
  amazonFeeTaxPct: pctField('Komisyon vergisi', 100).optional(),
  isTaxRegistered: z.boolean().optional(),
  salesTaxPct: pctField('Satış vergisi', 100).optional(),
  extraTaxPct: pctField('Ek vergi', 100).optional(),
  purchaseTaxPct: pctField('Alış vergisi', 100).optional(),
  commissionMode: commissionModeSchema.optional(),
  fixedCommissionPct: pctField('Komisyon oranı', 100).optional(),
  fxMode: fxModeSchema.optional(),
  fxManualRate: z.number().positive('Manuel kur pozitif olmalı.').nullable().optional(),
  fxSafetyMarginPct: pctField('Kur güvenlik marjı', 50).optional(),
  extraCostUsd: z.number().min(0, 'Ek maliyet negatif olamaz.').optional(),
  priceRounding: priceRoundingSchema.optional(),
  listingPriceMode: listingPriceModeSchema.optional(),
});
export type UpdatePricingProfileInput = z.infer<typeof updatePricingProfileSchema>;

export const pricingTierSchema = z
  .object({
    id: z.string().optional(),
    costFromUsd: z.number().min(0, 'Bant başlangıcı negatif olamaz.'),
    costToUsd: z.number().min(0, 'Bant bitişi negatif olamaz.').nullable(),
    minMarginPct: pctField('Minimum kâr'),
    stdMarginPct: pctField('Standart kâr'),
    maxMarginPct: pctField('Maksimum kâr'),
    singleSellerMarginPct: pctField('Tek satıcı kârı'),
    minQty: z
      .number()
      .int('Minimum adet tam sayı olmalı.')
      .min(0, 'Minimum adet negatif olamaz.')
      .default(1),
    defaultQty: z
      .number()
      .int('Adet tam sayı olmalı.')
      .min(0, 'Varsayılan adet negatif olamaz.')
      .default(10),
    sortOrder: z.number().int().default(0),
  })
  .refine((t) => t.costToUsd === null || t.costFromUsd < t.costToUsd, {
    message: 'Bant başlangıcı bitişinden küçük olmalı.',
    path: ['costToUsd'],
  })
  .refine((t) => t.minMarginPct <= t.stdMarginPct, {
    message: 'Minimum kâr, standart kârdan büyük olamaz.',
    path: ['minMarginPct'],
  })
  .refine((t) => t.stdMarginPct <= t.maxMarginPct, {
    message: 'Standart kâr, maksimum kârdan büyük olamaz.',
    path: ['stdMarginPct'],
  })
  .refine((t) => t.singleSellerMarginPct >= t.stdMarginPct, {
    message: 'Tek satıcı kârı, standart kârdan küçük olamaz.',
    path: ['singleSellerMarginPct'],
  });
export type PricingTierInput = z.infer<typeof pricingTierSchema>;

export const savePricingTiersSchema = z.object({
  tiers: z.array(pricingTierSchema).min(1, 'En az bir kâr bandı tanımlamalısınız.'),
});
export type SavePricingTiersInput = z.infer<typeof savePricingTiersSchema>;

/** §20.2 — `POST /pricing/simulate` — Amazon'a hiçbir şey göndermeden hesap yapar. */
/**
 * Boş bırakılan metin kutusu `""` gönderir. Opsiyonel ASIN alanında bu geçerli
 * sayılmalı — aksi halde kullanıcı hiçbir şey yazmadığı halde "geçerli ASIN girin"
 * hatası görür. `^$` alternatifi bunu çözerken tip çıkarımını da bozmaz.
 */
const OPTIONAL_ASIN_RE = /^$|^(B[0-9A-Z]{9}|[0-9]{9}[0-9X])$/i;

export const simulatePriceSchema = z.object({
  asin: z
    .string()
    .regex(OPTIONAL_ASIN_RE, 'Geçerli bir ASIN girin (ör. B08XYZ1234) veya boş bırakın.')
    .optional(),
  sourcePriceUsd: z
    .number({ message: 'Alış fiyatı sayı olmalı.' })
    .positive('Alış fiyatı sıfırdan büyük olmalı.'),
  shippingCostUsd: z
    .number({ message: 'Kargo ücreti sayı olmalı.' })
    .min(0, 'Kargo ücreti negatif olamaz.'),
  categoryName: z.string().optional(),
  amazonFeePct: z.number().min(0).max(100).nullable().optional(),
});
export type SimulatePriceInput = z.infer<typeof simulatePriceSchema>;

/** §9.1 — Kargo formülü tahmini denemesi. */
export const estimateShippingSchema = z.object({
  weightLbs: z.number().positive('Ağırlık sıfırdan büyük olmalı.').nullable().optional(),
  lengthIn: z.number().positive('Boy sıfırdan büyük olmalı.').nullable().optional(),
  widthIn: z.number().positive('En sıfırdan büyük olmalı.').nullable().optional(),
  heightIn: z.number().positive('Yükseklik sıfırdan büyük olmalı.').nullable().optional(),
});
export type EstimateShippingInput = z.infer<typeof estimateShippingSchema>;

/**
 * Boş bırakılan sayısal alanı "verilmedi" say.
 *
 * ⚠️ Bu olmadan ASIN kuralı ekranı HİÇ kullanılamıyordu.
 *
 * Tarayıcıda boş bir sayı kutusu `valueAsNumber` ile **NaN** üretiyor.
 * `z.number()` NaN'ı reddediyor, dolayısıyla kullanıcı "sadece standart kârı
 * değiştir" demek istediğinde form gönderilmiyor ve boş bıraktığı dokuz alanın
 * altında kırmızı "... sayı olmalı." yazıları beliriyordu. Oysa ekranda yazan
 * söz şuydu: "Boş bırakılan alanlar kâr bandından gelir."
 *
 * Kullanıcının kaydedebilmek için o dokuz alana değer uydurması gerekiyordu —
 * ve uydurduğu her değer o ürün için gerçek bir ezmeye dönüşüyordu (kargo ve
 * komisyon ezmesi fiyat hesabına doğrudan giriyor). Yani ya kural hiç
 * kaydedilemiyor ya da istenmeyen beş-altı ezme birden devreye giriyordu.
 * Üretimde `asin_rules` 0 satır: ekran bugüne kadar hiç kullanılamamış.
 *
 * Temizlik ŞEMADA yapılıyor, ekranda değil: hem tarayıcı hem sunucu aynı
 * kuralı uygular ve ileride başka bir ekran aynı şemayı kullanınca da doğru
 * davranır.
 *
 * `z.preprocess` yerine birleşim kullanılıyor: preprocess girdi tipini
 * `unknown`a çeviriyor ve react-hook-form çözümleyicisinin tipiyle uyuşmuyor.
 * Birleşimde girdi tipi `number | null | undefined` olarak kalıyor.
 *
 * ⚠️ Asıl şema ÖNDE. İki seçenek de reddedince çözümleyici ilkinin mesajını
 * gösteriyor; NaN seçeneği öndeyken 2,5 adet için ekranda "Invalid input:
 * expected NaN, received number" yazıyordu (ölçüldü, 3 Kasım 2026).
 */
const bosNumara = <T extends z.ZodTypeAny>(sema: T) =>
  z.union([sema, z.nan().transform(() => undefined)]);

/**
 * Tutar en küçük birimde tam sayı. Kesirli gelmesi, girilen metnin para biriminin ondalığından
 * fazla basamak taşıdığını gösteriyor (bkz. `paraAlani`): "1.299" yuvarlanırsa SAR 1,30 olurdu.
 */
const tutarAlani = (ad: string) =>
  z
    .number()
    .int('Geçersiz tutar. Binlik ayırıcı kullanmadan yazın (ör. 1299 ya da 1299,50).')
    .positive(`${ad} sıfırdan büyük olmalı.`);

/**
 * §7.3 — ASIN'e özel kuralın alanları (ASIN'siz): tekil kayıt, toplu kayıt ve ekrandaki form
 * paylaşıyor. Sıra denetimi eklenmiş şemalardan alan çıkarılamıyor (`omit` hata atıyor).
 */
export const asinKuraliAlanlariSchema = z.object({
  minMarginPct: bosNumara(pctField('Minimum kâr').nullable().optional()),
  stdMarginPct: bosNumara(pctField('Standart kâr').nullable().optional()),
  maxMarginPct: bosNumara(pctField('Maksimum kâr').nullable().optional()),
  singleSellerMarginPct: bosNumara(pctField('Tek satıcı kârı').nullable().optional()),
  minPriceJpy: bosNumara(tutarAlani('Min fiyat').nullable().optional()),
  maxPriceJpy: bosNumara(tutarAlani('Max fiyat').nullable().optional()),
  fixedPriceJpy: bosNumara(tutarAlani('Sabit fiyat').nullable().optional()),
  qty: bosNumara(
    z.number().int('Adet tam sayı olmalı.').min(0, 'Adet negatif olamaz.').nullable().optional(),
  ),
  handlingTimeDays: bosNumara(
    z
      .number()
      .int('Hazırlama süresi tam gün olmalı.')
      .min(0, 'Hazırlama süresi negatif olamaz.')
      .max(60, 'Hazırlama süresi en fazla 60 gün olabilir.')
      .nullable()
      .optional(),
  ),
  shippingCostUsdOverride: bosNumara(
    z.number().min(0, 'Kargo ücreti negatif olamaz.').nullable().optional(),
  ),
  referralFeePctOverride: bosNumara(pctField('Komisyon oranı', 100).nullable().optional()),
  repricerDisabled: z.boolean().optional(),
  isActive: z.boolean().optional(),
  note: z.string().max(500, 'Not en fazla 500 karakter olabilir.').nullable().optional(),
});
type AsinKuraliAlanlari = z.infer<typeof asinKuraliAlanlariSchema>;

/**
 * Kuralın KENDİ marjları sıralı olmalı (kâr bandı şemasındaki kuralın aynısı). Kuralın girmediği
 * marjlar banttan geliyor ve burada bilinmiyor; onları hesap sıraya itiyor (`resolveTargets`).
 */
export function kuralMarjSirasiniDenetle(k: AsinKuraliAlanlari, ctx: z.RefinementCtx): void {
  const buyukOlamaz = (
    alt: number | null | undefined,
    ust: number | null | undefined,
    alan: keyof AsinKuraliAlanlari,
    message: string,
  ) => {
    if (alt !== null && alt !== undefined && ust !== null && ust !== undefined && alt > ust) {
      ctx.addIssue({ code: 'custom', message, path: [alan] });
    }
  };
  const { minMarginPct: min, stdMarginPct: std, maxMarginPct: max } = k;
  const tek = k.singleSellerMarginPct;
  buyukOlamaz(min, std, 'minMarginPct', 'Minimum kâr, standart kârdan büyük olamaz.');
  buyukOlamaz(min, max, 'minMarginPct', 'Minimum kâr, maksimum kârdan büyük olamaz.');
  buyukOlamaz(std, max, 'stdMarginPct', 'Standart kâr, maksimum kârdan büyük olamaz.');
  buyukOlamaz(std, tek, 'singleSellerMarginPct', 'Tek satıcı kârı, standart kârdan küçük olamaz.');
  buyukOlamaz(min, tek, 'singleSellerMarginPct', 'Tek satıcı kârı, minimum kârdan küçük olamaz.');
}

/** Kuralın ürüne etki eden hiçbir değeri yoksa kayıt boştur (not tek başına bir şey değiştirmez). */
export function asinKuraliBosMu(k: AsinKuraliAlanlari): boolean {
  const degerler = [
    k.minMarginPct,
    k.stdMarginPct,
    k.maxMarginPct,
    k.singleSellerMarginPct,
    k.minPriceJpy,
    k.maxPriceJpy,
    k.fixedPriceJpy,
    k.qty,
    k.handlingTimeDays,
    k.shippingCostUsdOverride,
    k.referralFeePctOverride,
  ];
  return k.repricerDisabled !== true && degerler.every((d) => d === null || d === undefined);
}

export const BOS_ASIN_KURALI_MESAJI =
  'Kural boş: en az bir değer girin ya da "repricer\'ı kapat" kutusunu işaretleyin.';

function asinKuraliniDenetle(k: AsinKuraliAlanlari, ctx: z.RefinementCtx): void {
  kuralMarjSirasiniDenetle(k, ctx);
  if (asinKuraliBosMu(k)) ctx.addIssue({ code: 'custom', message: BOS_ASIN_KURALI_MESAJI });
}

const asinAlani = () =>
  z.string().regex(/^(B[0-9A-Z]{9}|[0-9]{9}[0-9X])$/i, 'Geçerli bir ASIN girin.');

/** §7.3 — ASIN'e özel kural. */
export const upsertAsinRuleSchema = asinKuraliAlanlariSchema
  .extend({ asin: asinAlani() })
  .superRefine(asinKuraliniDenetle);
export type UpsertAsinRuleInput = z.infer<typeof upsertAsinRuleSchema>;

/** Toplu ASIN kuralında bir istekte en fazla bu kadar ASIN. */
export const TOPLU_ASIN_KURALI_SINIRI = 20_000;

const asinListesiAlani = () =>
  z
    .array(asinAlani())
    .min(1, 'En az bir ASIN girin.')
    .max(TOPLU_ASIN_KURALI_SINIRI, `Tek seferde en fazla ${TOPLU_ASIN_KURALI_SINIRI} ASIN.`);

/**
 * Aynı kuralı birden çok ASIN'e uygula (sahip, 28 Eyl 2026: "ASIN'e özel kural
 * kısmında ASIN'i tek tek girebiliyoruz, toplu ASIN için de kural oluşturulabilsin").
 */
export const bulkUpsertAsinRuleSchema = asinKuraliAlanlariSchema
  .extend({ asins: asinListesiAlani() })
  .superRefine(asinKuraliniDenetle);
export type BulkUpsertAsinRuleInput = z.infer<typeof bulkUpsertAsinRuleSchema>;

/** Bu ASIN'lerden kaçının kuralı var? (üzerine yazma uyarısı ve toplu silme önizlemesi) */
export const asinKuraliKontrolSchema = z.object({ asins: asinListesiAlani() });
export type AsinKuraliKontrolInput = z.infer<typeof asinKuraliKontrolSchema>;

/** `beklenen`: önizlemedeki sayı; sunucu yeniden sayar, tutmazsa hiçbir şey silmez. */
export const asinKuraliTopluSilSchema = z.object({
  asins: asinListesiAlani(),
  beklenen: z.number().int().min(0),
});
export type AsinKuraliTopluSilInput = z.infer<typeof asinKuraliTopluSilSchema>;

/** Excel/CSV ile kural yüklemede tek dosyadaki en fazla satır. */
export const ASIN_KURALI_DOSYA_SATIR_SINIRI = 100_000;

/** Ekrana dönen en fazla hatalı satır; toplamı `hatali`. */
export const ASIN_KURALI_HATA_LISTESI_SINIRI = 200;

/**
 * `POST /asin-rules/import`: Excel ya da CSV dosyası base64 ile (en fazla ~15 MB).
 * `beklenen`: önizlemedeki sayılar; sunucu yeniden sayar, tutmazsa hiçbir şey yazmaz.
 */
export const asinKuraliDosyaSchema = z.object({
  ad: z.string().trim().min(1).max(200),
  veri: z.string().min(1).max(20_000_000),
  beklenen: z
    .object({ yeni: z.number().int().min(0), guncellenecek: z.number().int().min(0) })
    .optional(),
});
export type AsinKuraliDosyaInput = z.infer<typeof asinKuraliDosyaSchema>;

export interface AsinKuraliDosyaHatasi {
  /** Dosyadaki satır numarası (başlık 1. satır). */
  satir: number;
  asin: string | null;
  sebep: string;
}

/** Önizlemenin cevabı: `gecerli` = `yeni` + `guncellenecek` + `degismeyen`. */
export interface AsinKuraliDosyaOzeti {
  /** Veri satırları; şablonun örnek satırları sayılmıyor. */
  toplamSatir: number;
  gecerli: number;
  hatali: number;
  yeni: number;
  guncellenecek: number;
  /** Kuralı var ama dosyadaki değerler aynı ya da satır boş: dokunulmuyor. */
  degismeyen: number;
  ornekAtlanan: number;
  /** İlk `ASIN_KURALI_HATA_LISTESI_SINIRI` hatalı satır. */
  hatalar: AsinKuraliDosyaHatasi[];
  okunanSutunlar: string[];
  /** Başlığı tanınmadığı için okunmayan sütunlar. */
  okunmayanSutunlar: string[];
  /** Kuralı değişecek olup mağazada bulunan ürünler. */
  magazadakiUrun: number;
  /** Fiyatı alış fiyatı gelince hesaplanacak ürünler. */
  alisFiyatiOlmayan: number;
  /** Sabit/min fiyatın altında kaldığı maliyeti bilinen ürünler. */
  maliyetAltinda: number;
}

export interface AsinKuraliDosyaSonucu extends AsinKuraliDosyaOzeti {
  eklenen: number;
  guncellenen: number;
  yenidenHesaplanan: number;
  /** Kuyruk doluysa sebep; fiyatlar gece toplu hesabında güncellenir. */
  hesapErtelendi: string | null;
}

/**
 * Yapıştırılan metinden ASIN listesi: satır, virgül, noktalı virgül, sekme ya da
 * boşlukla ayrılmış olabilir; Excel'den kopyalanan sütun da çalışır. Büyük harfe
 * çevrilir, tekrarlar atılır, geçersizler ayrı döner (sessizce yutulmaz).
 */
export function asinListesiAyristir(metin: string): { gecerli: string[]; gecersiz: string[] } {
  const gecerli = new Set<string>();
  const gecersiz = new Set<string>();
  for (const parca of metin.split(/[\s,;]+/)) {
    const a = parca.trim().toUpperCase();
    if (a === '') continue;
    if (/^(B[0-9A-Z]{9}|[0-9]{9}[0-9X])$/.test(a)) gecerli.add(a);
    else gecersiz.add(parca.trim());
  }
  return { gecerli: [...gecerli], gecersiz: [...gecersiz] };
}

/** §EK-A — komisyon tablosu satırı düzenleme. */
export const updateFeeTableRowSchema = z.object({
  feePct: pctField('Komisyon oranı', 100),
  minFee: z.number().min(0).nullable(),
  priceThreshold: z.number().min(0).nullable(),
  feePctAbove: pctField('Eşik üstü oran', 100).nullable(),
  note: z.string().max(300).nullable().optional(),
});
export type UpdateFeeTableRowInput = z.infer<typeof updateFeeTableRowSchema>;
